import { ArrowRight, BookOpen, CheckCircle2, Code2, Download, LoaderCircle, UploadCloud, Wrench } from "lucide-react";
import { useState } from "react";
import { STANDARD_STATE_SPECS, type StandardState } from "codexpetlab-core";
import { DirectionPlayground, SpritePlayer, downloadBlob, exportStateGif } from "codexpetlab-player";
import type { SeoContentPage as SeoContentPageData } from "../data/seo-content";
import { getSeoContentPage } from "../data/seo-content";

const sampleSpritesheet = "/api/pets/miso-byte/spritesheet";

function InteractiveToolDemo({ path }: { path: string }) {
  const [state, setState] = useState<StandardState>("idle");
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  if (path === "/tools/codex-pet-validator") {
    return <section className="seo-tool-demo validator-demo"><div><span className="eyebrow">Try the local validator</span><h2>Validate pet.json and spritesheet.webp</h2><p>Files stay in your browser until you explicitly submit a package for community review.</p></div><a className="primary-button" href="/library"><UploadCloud size={17} /> Open My Library</a></section>;
  }

  if (path === "/tools/direction-playground") {
    return <section className="seo-tool-demo"><div className="seo-demo-heading"><span className="eyebrow">Live V2 sample</span><h2>Move around Miso Byte</h2><p>Move your pointer around the circle. The displayed frame comes directly from rows 9–10.</p></div><div className="seo-direction-demo"><DirectionPlayground src={sampleSpritesheet} width={360} aria-label="Miso Byte direction playground" /></div></section>;
  }

  if (path === "/tools/spritesheet-player" || path === "/tools/gif-exporter") {
    const exportGif = async () => {
      setExporting(true);
      setStatus(null);
      try {
        const blob = await exportStateGif({ src: sampleSpritesheet, state });
        const signature = new TextDecoder().decode(new Uint8Array(await blob.slice(0, 6).arrayBuffer()));
        if (signature !== "GIF89a") throw new Error("GIF encoding failed");
        downloadBlob(blob, `miso-byte-${state}.gif`);
        setStatus(`Saved ${(blob.size / 1024).toFixed(1)} KB GIF`);
      } catch (error) {
        setStatus(error instanceof Error ? error.message : "GIF export failed");
      } finally {
        setExporting(false);
      }
    };

    return <section className="seo-tool-demo"><div className="seo-demo-heading"><span className="eyebrow">Live spritesheet sample</span><h2>Play Miso Byte's real frames</h2><p>Select a standard state. The canvas uses the documented frame count and timing for that row.</p></div><div className="seo-player-demo"><SpritePlayer src={sampleSpritesheet} state={state} width={320} aria-label={`Miso Byte ${state} animation`} /><div className="seo-player-controls">{STANDARD_STATE_SPECS.map((spec) => <button key={spec.key} className={state === spec.key ? "active" : ""} type="button" onClick={() => setState(spec.key)}>{spec.label}</button>)}</div>{path === "/tools/gif-exporter" && <div className="seo-gif-action"><button className="primary-button" type="button" disabled={exporting} onClick={() => void exportGif()}>{exporting ? <LoaderCircle className="spin" size={17} /> : <Download size={17} />} {exporting ? "Exporting…" : `Export ${state} GIF`}</button>{status && <span role="status">{status}</span>}</div>}</div></section>;
  }

  return null;
}

export function SeoContentPage({ page }: { page: SeoContentPageData }) {
  const Icon = page.kind === "tool" ? Wrench : page.kind === "docs" ? Code2 : BookOpen;
  return (
    <article className="seo-content-page">
      <header className="seo-content-hero">
        <p className="eyebrow"><Icon size={14} /> {page.eyebrow}</p>
        <h1>{page.heading}</h1>
        <p>{page.intro}</p>
      </header>
      <InteractiveToolDemo path={page.path} />
      <div className="seo-content-layout">
        <div className="seo-content-sections">
          {page.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}><CheckCircle2 size={16} /> <span>{bullet}</span></li>)}</ul>}
              {section.code && <pre><code>{section.code}</code></pre>}
            </section>
          ))}
        </div>
        <aside className="seo-related-card">
          <span className="filter-label">Related resources</span>
          {page.relatedPaths.map((path) => {
            const related = getSeoContentPage(path);
            const label = related?.heading ?? (path === "/pets" ? "Browse Codex pets" : path === "/library" ? "Open My Library" : path);
            return <a key={path} href={path}>{label}<ArrowRight size={15} /></a>;
          })}
        </aside>
      </div>
    </article>
  );
}
